import * as React from "react";
import {
    CSSProperties,
    ReactNode,
    createContext,
    memo,
    useContext,
    useMemo,
} from "react";
import { getDateItemsForRange } from "./timelineUtils";
import { Base } from "../../framework/base";
import { useAsyncThunkAction } from "../../hooks/useAsyncThunkAction";
import { fetchCalendarDays } from "../../store/calendarSlice";
import { useAppSelector } from "../../framework/customStore";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddIcon from "@mui/icons-material/Add";
import { useCellSelection } from "../../hooks/timelineGrid/useCellSelection";
import MuiMenu, { IMuiMenuItemProps } from "../framework/muiMenu";
import { Button } from "@mui/material";

/*

Renders a grid where columns are generated for each date within a given range.
Cells are automatically placed within the grid based on the column prop given.

Usage:

<TimelineGrid
    startDate={startDate}
    endDate={endDate}
>
    <TimelineGridRow infoCell={<div>info box</div>}>

        <TimelineGridCell column={date1}>
            Cell for date1
        </TimelineGridCell>

        <TimelineGridCell column={date2}>
            Cell for date2
        </TimelineGridCell>

    </TimelineGridRow>
</TimelineGrid>

*/

export const DATE_COLUMN_WIDTH = 180;
const DATE_COLUMN_WIDTH_PX = `${DATE_COLUMN_WIDTH}px`;

interface TimelineGridContext {
    columnCount: number;
    getGridDates: () => string[];
    getColumnForDate: (date: string, exact?: boolean) => number;
    isOverlapping: (startDate: string, endDate: string) => boolean;
}

export const TimelineGridContext = createContext<TimelineGridContext | null>(null);

/**
 * Renders the add actions button in the timeline header.
 * If there is only one action, it renders a Button.
 * If there are multiple actions, it renders a MuiMenu with all actions.
 */
const AddActionButton = ({ addActions }: { addActions?: IMuiMenuItemProps[] }) => {
    if (!addActions || addActions.length === 0) return null;

    if (addActions.length === 1) {
        const action = addActions[0];
        return (
            <Button
                size="small"
                variant="contained"
                startIcon={action.icon || <AddIcon />}
                onClick={action.onClick}
            >
                {action.label}
            </Button>
        );
    }

    return (
        <MuiMenu
            label={<AddCircleIcon fontSize="large" color="primary" />}
            items={addActions.map(a => ({
                label: a.label,
                icon: a.icon,
                onClick: a.onClick,
            }))}
            size="large"
            buttonSx={{ p: 0 }}
        />
    );
};

interface TimelineGridProps {
    startDate: string;
    endDate: string;
    addActions: IMuiMenuItemProps[];
    headerActionsNode?: ReactNode;
    children?: ReactNode;
}

/**
 * This component should be used within a wrapper component (e.g., VehicleTimelinePlannerList).
 */
export const TimelineGrid = memo((props: TimelineGridProps) => {
    const dateRange = useMemo(
        () => getDateItemsForRange(props.startDate, props.endDate),
        [props.startDate, props.endDate]
    );

    useAsyncThunkAction(
        () =>
            fetchCalendarDays({
                startDate: props.startDate,
                endDate: props.endDate,
            }),
        { onError: () => null },
        [props.startDate, props.endDate]
    );

    const todayDate = Base.dayjsToJsonDate(new Date());

    const context: TimelineGridContext = useMemo(
        () => ({
            columnCount: dateRange.length,
            // + 1 to account for the info cell
            getColumnForDate: (d, exact = false) => {
                const date = Base.dayjsToJsonDate(d);
                const index =
                    dateRange.findIndex((dr) => dr.dateString === date) + 1;

                if (!exact && !index) {
                    // if date is before range, return first column
                    if (dateRange[0]?.dateString > date) return 1;
                    // Otherwise last column
                    return dateRange.length;
                }

                return index;
            },
            getGridDates: () => dateRange.map((dr) => dr.dateString),
            isOverlapping: (s, e) =>
                Base.isOverlappingDate(
                    dateRange[0]?.dateString,
                    dateRange[dateRange.length - 1]?.dateString,
                    s,
                    e,
                    true,
                    "day"
                ),
        }),
        [dateRange]
    );

    const calendarDays = useAppSelector((state) => state.calendar.entities);

    return (
        <TimelineGridContext.Provider value={context}>
            <div className="timeline-grid-container" style={{ height: "100%" }}>
                <div className="timeline-grid-row-container timeline-grid-sticky-header">
                    <div className="timeline-grid-info-container timeline-grid-sticky-header-column">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                flexWrap: "nowrap",
                                whiteSpace: "nowrap",
                            }}
                        >
                            <AddActionButton addActions={props.addActions} />

                            {props.headerActionsNode ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4,
                                        flex: "0 0 auto",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {props.headerActionsNode}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div
                        className="timeline-grid-row timeline-grid-dates"
                        style={getRowGridStyle(context.columnCount)}
                    >
                        {dateRange.map((d) => {
                            const isToday = d.dateString === todayDate;
                            const isHoliday =
                                calendarDays[d.dateString]?.isHoliday ?? false;
                            return (
                                <div
                                    style={{ position: "relative" }}
                                    className={`timeline-grid-date timeline-grid-cell bold ${
                                        isToday ? "timeline-grid-today" : ""
                                    } ${
                                        isHoliday ? "timeline-grid-holiday" : ""
                                    }`}
                                    key={d.dateString}
                                >
                                    {d.localizedDateString}
                                </div>
                            );
                        })}
                        <div className="timeline-grid-cell" />
                    </div>
                </div>
                <div>{props.children}</div>
            </div>
        </TimelineGridContext.Provider>
    );
});

interface TimelineGridRowProps {
    infoColumn: ReactNode;
    renderColumnHeader?: (date: string) => ReactNode;
    children: ReactNode;
    cellSelection?: {
        targetId: string;
        selectedCell?: { day: string; targetId: string } | null;
        onCellSelect: (cell: { day: string; targetId: string }, event: React.MouseEvent) => void;
    };
    rowHeight?: number;
}

export const TimelineGridRow = (props: TimelineGridRowProps) => {
    const context = useContext(TimelineGridContext);
    const { handleGridClick, backgroundCells } = useCellSelection(props.cellSelection);
    const computedHeight = props.rowHeight ? Math.max(40, props.rowHeight) : undefined;

    return (
        <div className="timeline-grid-row-container">
            <div className="timeline-grid-info-container timeline-grid-sticky-column">
                {props.infoColumn}
            </div>
            <div className="timeline-grid-row-cells-container">
                {props.renderColumnHeader && (
                    <div>
                        {context.getGridDates().map((date, i) => (
                            <div
                                style={{ width: DATE_COLUMN_WIDTH_PX }}
                                key={i}
                                className="timeline-grid-header timeline-grid-cell"
                            >
                                {props.renderColumnHeader(date)}
                            </div>
                        ))}
                    </div>
                )}

                <div
                    className="timeline-grid-row"
                    style={{
                        ...getRowGridStyle(context.columnCount),
                        position: "relative",
                        minHeight: computedHeight,
                        height: computedHeight,
                    }}
                    onClick={handleGridClick}
                >
                    {backgroundCells}
                    {props.children}
                </div>
            </div>
        </div>
    );
};

function getRowGridStyle(columnCount: number): CSSProperties {
    return {
        gridTemplateColumns: `repeat(${columnCount}, ${DATE_COLUMN_WIDTH_PX})`,
    };
}
